import Link from 'next/link';
import Image from 'next/image';
import { Plane, Phone, Mail, MapPin, Globe } from 'lucide-react';

const QUICK_LINKS = [
  { label: 'Sobre Nosotros',      href: '/sobre-nosotros' },
  { label: 'Contacto',            href: '/contacta-con-nosotros' },
  { label: 'Blogs',               href: '/blog' },
  { label: 'Artículos',           href: '/articulos' },
  { label: 'Preguntas Frecuentes', href: '/preguntas-frecuentes' },
  { label: 'Vuelos',              href: '/vuelos' },
];

const LEGAL_LINKS = [
  { label: 'Política de Privacidad', href: '/politica-de-privacidad' },
  { label: 'Términos y condiciones',  href: '/terminos-y-condiciones' },
  { label: 'Política de Cookies',    href: '/politica-de-cookies' },
  { label: 'Política de Reembolso',  href: '/politica-de-reembolso' },
  { label: 'Descargo de Responsabilidad', href: '/descargo-de-responsabilidad' },
];

export default function Footer() {
  return (
    <footer
      className="relative text-slate-900 overflow-hidden"
      style={{ background: '#1a1a1a' }}
    >
      {/* City skyline silhouette background */}
      <div
        className="absolute inset-0 opacity-10 bg-no-repeat bg-bottom bg-contain pointer-events-none"
      />

      {/* Why Choose section */}
      <div className="relative border-b border-white/10" style={{ background: '#1e2535' }}>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h3 className="text-white font-bold text-lg mb-2">¿Por qué elegir Vuelosexpertos?</h3>
          <p className="text-slate-400 text-sm leading-relaxed max-w-none">
            En Vuelosexpertos hacemos que reservar tu próximo vuelo sea rápido, sencillo y confiable. Te ayudamos a encontrar las mejores opciones al mejor precio, con el respaldo de un equipo de expertos listo para asesorarte en todo momento. Disfruta de un proceso de reserva seguro, ofertas exclusivas y un servicio pensado para que viajes con total tranquilidad. Empieza a planear tu próxima aventura con Vuelosexpertos hoy mismo.
          </p>
        </div>
      </div>

      {/* Main footer grid */}
      <div className="relative max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* QUICK LINKS */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5 border-b border-white/10 pb-2">Enlaces Rápidos</h4>
            <ul className="space-y-3">
              {QUICK_LINKS.map(link => (
                <li key={link.label}>
                  <Link href={link.href} className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <span className="w-1 h-1 rounded-full bg-sky-500 shrink-0 group-hover:bg-white transition-colors" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* LEGAL */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5 border-b border-white/10 pb-2">Aspectos Legales</h4>
            <ul className="space-y-3">
              {LEGAL_LINKS.map(link => (
                <li key={link.label}>
                  <Link href={link.href} className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <span className="w-1 h-1 rounded-full bg-sky-500 shrink-0 group-hover:bg-white transition-colors" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CONTACT */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5 border-b border-white/10 pb-2">Contacto</h4>
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-slate-300 mb-2 font-medium">¿Necesita ayuda? Envíenos un correo electrónico</p>
                <a href="mailto:info@vuelosexpertos.com"
                  className="flex items-center gap-2 text-sky-400 hover:text-sky-300 transition-colors font-semibold">
                  <Mail size={14} className="shrink-0" />
                  info@vuelosexpertos.com
                </a>
                <a href="mailto:support@vuelosexpertos.com"
                  className="flex items-center gap-2 text-sky-400 hover:text-sky-300 transition-colors font-semibold mt-1.5">
                  <Mail size={14} className="shrink-0" />
                  support@vuelosexpertos.com
                </a>
              </div>
              <div>
                <p className="text-slate-300 mb-2 font-medium">Llámenos</p>
                <a href="tel:+529986090037"
                  className="flex items-center gap-2 text-sky-400 hover:text-sky-300 transition-colors font-semibold">
                  <Phone size={14} className="shrink-0" />
                  +52-998-609-0037
                </a>
              </div>
              <div>
                <p className="text-slate-300 mb-2 font-medium">Dirección</p>
                <div className="flex items-start gap-2 text-sky-400">
                  <MapPin size={14} className="shrink-0 mt-0.5" />
                  <span className="text-sky-400">9392 Avenida Juárez, Guerrero, Cancún 67398, Mexico</span>
                </div>
              </div>
            </div>
          </div>

          {/* ABOUT BOOKGUIDENOW */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5 border-b border-white/10 pb-2">Sobre Vuelosexpertos</h4>

            {/* Logo */}
            <div className="mb-4 flex gap-3 items-center">
              <Link href="/" aria-label="Go to homepage" className='flex gap-2 items-center'>
                <div className='icon-wrap bg-white rounded-lg p-3'>
                  <Image
                    src="/Vuelosexpertos.svg"
                    alt="Vuelosexpertos"
                    width={210}
                    height={34}
                  />
                </div>
              </Link>
            </div>

            <p className="text-slate-400 text-xs leading-relaxed mb-5">
              Vuelosexpertos está aquí para ayudarte a viajar más y preocuparte menos. Encuentra vuelos al mejor precio, recibe asesoría personalizada de nuestros expertos y disfruta de un proceso de reserva rápido, fácil y seguro. Desde el primer clic hasta el despegue, te acompañamos para que tu experiencia sea sencilla, confiable y llena de emoción. Tu próximo viaje comienza con Vuelosexpertos.
            </p>

            {/* Email subscribe */}
            <div className="flex gap-2 mb-5">
              <label htmlFor="footer-newsletter-email" className="sr-only">Dirección de correo electrónico</label>
              <input
                id="footer-newsletter-email"
                type="email"
                name="email"
                placeholder="Ingrese su correo electrónico"
                autoComplete="email"
                className="flex-1 bg-white text-slate-700 border border-slate-200 px-3 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
              />
              <button type="button" aria-label="Subscribe to newsletter" className="bg-sky-500 hover:bg-sky-600 text-white p-2.5 rounded-xl transition-colors shrink-0">
                <Mail size={16} />
              </button>
            </div>

            {/* Social icons */}
            <div className="flex gap-3">
              {/* Pinterest */}
              <a href="#" className="w-8 h-8 rounded-full bg-sky-500 hover:bg-sky-400 flex items-center justify-center transition-colors" aria-label="Pinterest">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.08 3.16 9.44 7.63 11.2-.11-.97-.2-2.46.04-3.52.22-.96 1.46-6.19 1.46-6.19s-.37-.75-.37-1.86c0-1.74 1.01-3.04 2.27-3.04 1.07 0 1.59.8 1.59 1.77 0 1.07-.69 2.68-1.04 4.17-.29 1.24.62 2.25 1.84 2.25 2.21 0 3.91-2.33 3.91-5.69 0-2.97-2.14-5.05-5.19-5.05-3.54 0-5.61 2.65-5.61 5.39 0 1.07.41 2.21.92 2.83.1.12.11.23.08.35-.09.39-.3 1.24-.34 1.41-.05.23-.17.27-.4.16-1.49-.7-2.43-2.89-2.43-4.65 0-3.78 2.75-7.26 7.93-7.26 4.16 0 7.39 2.97 7.39 6.93 0 4.14-2.61 7.47-6.23 7.47-1.22 0-2.36-.63-2.75-1.38l-.75 2.78c-.27 1.04-1 2.34-1.49 3.13.9.28 1.87.43 2.87.43 6.63 0 12-5.37 12-12S18.63 0 12 0z"/></svg>
              </a>
              {/* X / Twitter */}
              <a href="#" className="w-8 h-8 rounded-full bg-sky-500 hover:bg-sky-400 flex items-center justify-center transition-colors" aria-label="X">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              {/* Instagram */}
              <a href="#" className="w-8 h-8 rounded-full bg-sky-500 hover:bg-sky-400 flex items-center justify-center transition-colors" aria-label="Instagram">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              {/* YouTube */}
              <a href="#" className="w-8 h-8 rounded-full bg-sky-500 hover:bg-sky-400 flex items-center justify-center transition-colors" aria-label="YouTube">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-white/10" style={{ background: '#111' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <p className='text-sm'>&copy; 2026 Vuelosexpertos. Todos los derechos reservados.</p>
          <div className="flex items-center gap-4">
            {/* MasterCard */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 32" width="44" height="28" aria-label="Mastercard">
              <rect width="48" height="32" rx="4" fill="#1a1a1a" stroke="#333" strokeWidth="1"/>
              <circle cx="18" cy="16" r="10" fill="#eb001b" opacity="0.9"/>
              <circle cx="30" cy="16" r="10" fill="#f79e1b" opacity="0.9"/>
              <path d="M24 8.27a10 10 0 0 1 0 15.46A10 10 0 0 1 24 8.27z" fill="#ff5f00"/>
            </svg>
            {/* Visa */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 32" width="44" height="28" aria-label="Visa">
              <rect width="48" height="32" rx="4" fill="#fff" stroke="#ddd" strokeWidth="1"/>
              <text x="50%" y="22" textAnchor="middle" fill="#1a1f71" fontSize="14" fontWeight="bold" fontFamily="Arial">VISA</text>
            </svg>
            {/* Secure badge */}
            <div className="flex items-center gap-1.5 bg-green-800 text-green-200 text-xs font-semibold px-2.5 py-1.5 rounded-lg">
              <svg width="10" height="12" viewBox="0 0 10 12" fill="currentColor"><path d="M5 0L0 2v4c0 3.3 2.1 5.7 5 6 2.9-.3 5-2.7 5-6V2L5 0z"/></svg>
              100 % Seguro
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
